// pages/UsersPage.tsx
import { useState, useEffect } from "react";
import { Search, Plus, Eye, Edit, Trash2, Ban, UserCog, UserCheck, UserX } from "lucide-react";
import UsersList from "../../../components/features/users/UsersList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { User } from "../../../types/features/User";
import PageListLayout from "../../../layouts/PageListLayout";
import { useNavigate } from "react-router-dom";
import useUsers from "../../../hooks/features/users/useUsers";

export default function UsersPage() {
  const { 
    users, 
    loading, 
    pagination,
    getUsers,
    deleteUser,
    updateUser
  } = useUsers();
  
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<User[]>([]);

  // États de sélection
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Chargement initial des utilisateurs
  useEffect(() => {
    getUsers(1, 20);
  }, [getUsers]);

  const displayUsers = searchQuery.trim() ? searchResults : users;

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      const filtered = users.filter(user => 
        user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.phonenumber?.includes(searchQuery)
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  };

  const handlePhoneSearch = async () => {
    if (searchQuery.trim()) {
      const found = users.find(user => 
        user.phonenumber?.includes(searchQuery)
      );
      
      if (found) {
        navigate("/administration/users/details", { state: found });
      } else {
        alertError("Aucun utilisateur trouvé avec ce numéro");
      }
      setSearchQuery("");
      setSearchResults([]);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      try {
        const success = await deleteUser(id);
        if (success) {
          alertSuccess("Utilisateur supprimé avec succès !");
          await getUsers(pagination.currentPage, pagination.perPage);
        }
      } catch (error) {
        alertError("Erreur lors de la suppression de l'utilisateur");
        console.error(error);
      }
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    const newStatus = user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const action = newStatus === "ACTIVE" ? "activer" : "désactiver";
    
    if (window.confirm(`Êtes-vous sûr de vouloir ${action} cet utilisateur ?`)) {
      try {
        const result = await updateUser(user.id, { status: newStatus });
        if (result) {
          alertSuccess(`Utilisateur ${action} avec succès !`);
          await getUsers(pagination.currentPage, pagination.perPage);
        }
      } catch (error) {
        alertError(`Erreur lors de ${action} de l'utilisateur`);
        console.error(error);
      }
    }
  };

  const getActionsMenu = (user: User): Menu[] => {
    const isActive = user.status === "ACTIVE";
    
    return [
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/administration/users/edit", { state: user });
          setSelectedUser(null);
        },
      },
      {
        label: isActive ? "Désactiver" : "Activer",
        icon: isActive ? Ban : UserCheck,
        onClick: () => {
          handleToggleUserStatus(user);
          setSelectedUser(null);
        }
      },
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/administration/users/details", { state: user });
          setSelectedUser(null);
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          handleDeleteUser(user.id);
          setSelectedUser(null);
        },
      },
    ];
  };

  // Affichage du chargement initial
  if (loading && users.length === 0) {
    return (
      <PageListLayout title="">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#031B4E] border-t-transparent"></div>
        </div>
      </PageListLayout>
    );
  }

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Utilisateurs</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Gérez tous les utilisateurs de la plateforme
              </p>
              {pagination.total > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {pagination.total} utilisateur(s) au total
                </p>
              )}
            </div>
            
            <button
              onClick={() => navigate("/administration/users/new")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouvel utilisateur</span>
            </button>
          </div>

          {/* Zone de Recherche */}
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, email ou téléphone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  onClick={handleSearch}
                  className="flex-1 sm:flex-none px-5 py-2.5 bg-primary text-white font-medium text-sm rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all shadow-sm"
                >
                  Rechercher
                </button>
                <button
                  onClick={handlePhoneSearch}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#031B4E] text-white font-medium text-sm rounded-xl hover:bg-[#052975] focus:outline-none focus:ring-4 focus:ring-blue-600/10 transition-all shadow-sm"
                >
                  <UserCog size={14} />
                  <span>Par téléphone</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des utilisateurs */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <UsersList
            users={displayUsers}
            onAction={(user) => {
              setSelectedUser(user);
            }}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal volant */}
      {selectedUser && (
        <MenuModal
          title={`${selectedUser.first_name} ${selectedUser.last_name}` || "Actions utilisateur"}
          isOpen={true}
          onClose={() => setSelectedUser(null)}
          icon={null}
          menu={getActionsMenu(selectedUser)}
        />
      )}
    </PageListLayout>
  );
}